import { Box, Text, Stack, Group } from '@mantine/core'

/* ─── ADAPT Scores Mode (5-vertex pentagon) ─── */

const ADAPT_STAGES = [
  { key: 'awareness', label: 'Awareness', short: 'A' },
  { key: 'dynamics', label: 'Dynamics', short: 'D' },
  { key: 'action', label: 'Action', short: 'A' },
  { key: 'process', label: 'Process', short: 'P' },
  { key: 'transform', label: 'Transform', short: 'T' },
]

/* ─── Profile Signal Counts Mode (4-vertex diamond) ─── */

const SIGNAL_STAGES = [
  { key: 'mobilizer', label: 'Mobilizer', icon: '⚡' },
  { key: 'strategist', label: 'Strategist', icon: '🎯' },
  { key: 'stabilizer', label: 'Stabilizer', icon: '🛡️' },
  { key: 'connector', label: 'Connector', icon: '🔗' },
]

function getVertices(cx, cy, radius, count) {
  return Array.from({ length: count }, (_, i) => {
    const angle = ((i * (360 / count)) - 90) * (Math.PI / 180)
    return {
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle),
    }
  })
}

function polygonPoints(vertices) {
  return vertices.map((v) => `${v.x},${v.y}`).join(' ')
}

/**
 * Radar chart visualization.
 * mode="scores" — 5-vertex pentagon for ADAPT framework scores (0-100)
 * mode="counts" — 4-vertex diamond for profile signal counts (factual data)
 */
export function AdaptScoreRing({ scores, signals, mode = 'scores', compact = false, centerLabel }) {
  const isCounts = mode === 'counts'
  const stages = isCounts ? SIGNAL_STAGES : ADAPT_STAGES
  const vertexCount = stages.length

  // For counts mode, use signal data; for scores mode, use scores data
  const data = isCounts ? (signals || {}) : (scores || {})
  if (!data || Object.keys(data).length === 0) return null

  // For counts mode, normalize to max value instead of 0-100
  const maxValue = isCounts
    ? Math.max(...stages.map((s) => data[s.key] || 0), 1)
    : 100

  const size = compact ? 180 : 400
  const cx = size / 2
  const cy = size / 2
  const maxRadius = compact ? 70 : 120
  const labelRadius = compact ? 82 : 160

  // Grid rings at 25%, 50%, 75%, 100%
  const gridRings = [0.25, 0.5, 0.75, 1.0]
  const outerVertices = getVertices(cx, cy, maxRadius, vertexCount)
  const labelVertices = getVertices(cx, cy, labelRadius, vertexCount)

  // Data polygon
  const dataVertices = stages.map((stage, i) => {
    const value = data[stage.key] || 0
    const fraction = Math.min(maxValue, Math.max(0, value)) / maxValue
    const angle = ((i * (360 / vertexCount)) - 90) * (Math.PI / 180)
    return {
      x: cx + maxRadius * fraction * Math.cos(angle),
      y: cy + maxRadius * fraction * Math.sin(angle),
    }
  })

  // Center text
  const centerText = centerLabel || (isCounts ? null : data.overall)
  const centerSubtext = isCounts ? null : 'OVERALL'

  return (
    <Stack align="center" gap={compact ? 4 : 'sm'}>
      <Box style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {/* Grid rings */}
          {gridRings.map((fraction) => {
            const verts = getVertices(cx, cy, maxRadius * fraction, vertexCount)
            return (
              <polygon
                key={fraction}
                points={polygonPoints(verts)}
                fill="none"
                stroke="rgba(0, 229, 255, 0.08)"
                strokeWidth={1}
              />
            )
          })}

          {/* Axis lines from center to vertices */}
          {outerVertices.map((v, i) => (
            <line
              key={i}
              x1={cx}
              y1={cy}
              x2={v.x}
              y2={v.y}
              stroke="rgba(0, 229, 255, 0.06)"
              strokeWidth={1}
            />
          ))}

          {/* Data polygon — filled */}
          <polygon
            points={polygonPoints(dataVertices)}
            fill="rgba(0, 229, 255, 0.12)"
            stroke="rgba(0, 229, 255, 0.7)"
            strokeWidth={2}
            style={{ filter: 'drop-shadow(0 0 6px rgba(0, 229, 255, 0.3))' }}
          />

          {/* Dots at vertices */}
          {dataVertices.map((v, i) => (
            <circle
              key={i}
              cx={v.x}
              cy={v.y}
              r={compact ? 3 : 4}
              fill="#00e5ff"
              style={{ filter: 'drop-shadow(0 0 4px rgba(0, 229, 255, 0.6))' }}
            />
          ))}

          {/* Stage labels + values */}
          {stages.map((stage, i) => {
            const lv = labelVertices[i]
            const value = data[stage.key] || 0
            return (
              <g key={stage.key}>
                <text
                  x={lv.x}
                  y={lv.y - (compact ? 4 : 6)}
                  textAnchor="middle"
                  dominantBaseline="auto"
                  fill="rgba(0, 229, 255, 0.7)"
                  fontSize={compact ? 9 : 11}
                  fontFamily="Orbitron, sans-serif"
                  fontWeight={700}
                >
                  {compact ? (stage.short || stage.label.charAt(0)) : stage.label}
                </text>
                <text
                  x={lv.x}
                  y={lv.y + (compact ? 8 : 10)}
                  textAnchor="middle"
                  dominantBaseline="auto"
                  fill="rgba(224, 232, 240, 0.6)"
                  fontSize={compact ? 9 : 12}
                  fontFamily="Rajdhani, sans-serif"
                  fontWeight={600}
                >
                  {value}
                </text>
              </g>
            )
          })}

          {/* Center text */}
          {centerText != null && !compact && (
            <>
              <text
                x={cx}
                y={cy - (centerSubtext ? 6 : 0)}
                textAnchor="middle"
                dominantBaseline="auto"
                fill="#00e5ff"
                fontSize={isCounts ? 14 : 28}
                fontFamily="Orbitron, sans-serif"
                fontWeight={700}
                style={{ filter: 'drop-shadow(0 0 8px rgba(0, 229, 255, 0.5))' }}
              >
                {centerText}
              </text>
              {centerSubtext && (
                <text
                  x={cx}
                  y={cy + 14}
                  textAnchor="middle"
                  dominantBaseline="auto"
                  fill="rgba(224, 232, 240, 0.5)"
                  fontSize={9}
                  fontFamily="Orbitron, sans-serif"
                  fontWeight={600}
                  letterSpacing={2}
                >
                  {centerSubtext}
                </text>
              )}
            </>
          )}
        </svg>
      </Box>

      {/* Compact mode: show label below */}
      {compact && centerText != null && (
        <Group gap={6} justify="center">
          <Text size="xs" c="dimmed" ff="Orbitron, sans-serif" fw={600}>
            {isCounts ? 'SIGNALS' : 'ADAPT'}
          </Text>
          <Text size="sm" fw={700} ff="Orbitron, sans-serif" c="cyan.3">
            {centerText}
          </Text>
        </Group>
      )}
    </Stack>
  )
}
